import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';

import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';

/* Removal runs in two beats: a trolley carts the product off out of frame,
   then the row it left behind closes up. Both durations are mirrored in the
   CSS below — change them here and there together. */
const HAUL_MS = 2000;
const COLLAPSE_MS = 360;

function CartPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);

  /* IDs of cart items currently being carted off */
  const [haulingIds, setHaulingIds] = useState(() => new Set());

  /* IDs whose row is closing up after the dust has settled */
  const [collapsingIds, setCollapsingIds] = useState(() => new Set());

  /* IDs whose quantity change is in flight */
  const [updatingIds, setUpdatingIds] = useState(() => new Set());

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      const { data } = await axios.get(
        `${API}/cart/${userInfo._id}`
      );

      setItems(data);
    } catch (error) {
      console.log(error);
    }
  };

  /* Change a line's quantity. The server is the authority on stock, so a
     rejection rolls the row back to what it was. */
  const changeQuantity = async (item, nextQuantity) => {
    if (nextQuantity < 1 || updatingIds.has(item._id)) {
      return;
    }

    const previous = item.quantity;

    /* Optimistic — the stepper should feel instant */
    setItems((prev) =>
      prev.map((row) =>
        row._id === item._id ? { ...row, quantity: nextQuantity } : row
      )
    );

    setUpdatingIds((prev) => new Set(prev).add(item._id));

    try {
      await axios.put(`${API}/cart/${item._id}`, {
        quantity: nextQuantity
      });
    } catch (error) {
      console.log(error);

      setItems((prev) =>
        prev.map((row) =>
          row._id === item._id ? { ...row, quantity: previous } : row
        )
      );

      toast.error(error.response?.data?.message || 'Could not update quantity');
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(item._id);
        return next;
      });
    }
  };

  const removeItem = async (id) => {
    /* Already on its way out — ignore repeat clicks */
    if (haulingIds.has(id)) {
      return;
    }

    /* Let the trolley cart it off first */
    setHaulingIds((prev) => new Set(prev).add(id));

    /* Wait for it to leave frame (matches HAUL_MS in the CSS below) */
    await new Promise((resolve) => setTimeout(resolve, HAUL_MS));

    /* Then close the gap the card left behind, so the rows below glide up
       instead of jumping the moment the delete lands. */
    setCollapsingIds((prev) => new Set(prev).add(id));

    await new Promise((resolve) => setTimeout(resolve, COLLAPSE_MS));

    try {
      await axios.delete(`${API}/cart/${id}`);

      await fetchCart();
    } catch (error) {
      console.log(error);
    } finally {
      setHaulingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      setCollapsingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  /*
    The removal scene: a trolley rolls in and parks under the product, the
    product drops out of the card into it, and the trolley wheels away off
    the edge of the row with it.

    The product is a child of the trolley, so once it has landed the two
    move as one body — the trolley's travel is the only path in play, and
    there is nothing to keep in sync. Everything is choreographed against a
    single 1.5s timeline (HAUL_MS).
  */
  const renderHaul = (item) => (
    <div className="cart-haul-layer">
      <span className="cart-haul-road" />

      <span className="cart-haul-trolley">
        <span className="cart-haul-glyph">🛒</span>

        {/* The real product, dropping in and then riding along */}
        <img
          src={item.image}
          alt=""
          aria-hidden="true"
          className="cart-haul-product"
        />

        <span className="cart-haul-line cart-haul-line-1" />
        <span className="cart-haul-line cart-haul-line-2" />
      </span>
    </div>
  );

  const addToWishlist = async (item) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    if (!userInfo) {
      toast.error('Please Login First');

      return;
    }

    try {
      await axios.post(`${API}/wishlist`, {
        userId: userInfo._id,
        productId: item.productId,
        name: item.name,
        image: item.image,
        price: item.price
      });

      toast.success('Added To Wishlist ❤️');
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Failed To Add Wishlist');
    }
  };

  const total = items.reduce(
    (acc, item) => acc + item.price * item.quantity,

    0
  );

  return (
    <>
      <CustomNavbar />

      <div className="cart-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 cart-hero">
            <span className="cart-hero-blob cart-hero-blob-1" />
            <span className="cart-hero-blob cart-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1">🛒 Shopping Cart</h1>
                <p className="mb-0 cart-hero-text">
                  Review your selected products before checkout.
                </p>
              </div>
              <Badge className="cart-count-badge">
                {items.length} {items.length === 1 ? 'Item' : 'Items'}
              </Badge>
            </Card.Body>
          </Card>

          {items.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '20px' }}
            >
              <Card.Body className="cart-empty">
                {/* Animated scene: the trolley rolls in, catches a couple of
                    products, then bumps the Continue Shopping button. */}
                <div className="ce-stage">
                  {/* Products drifting in the background */}
                  <span className="ce-float ce-float-1">🎁</span>
                  <span className="ce-float ce-float-2">👟</span>
                  <span className="ce-float ce-float-3">🎧</span>
                  <span className="ce-float ce-float-5">⌚</span>

                  <div className="ce-track">
                    {/* Items dropping into the trolley as it passes */}
                    <span className="ce-drop ce-drop-1">🧴</span>
                    <span className="ce-drop ce-drop-2">📿</span>

                    <span className="ce-shadow" />

                    <span className="ce-trolley">
                      🛒
                      <span className="ce-spark ce-spark-1">✨</span>
                      <span className="ce-spark ce-spark-2">✨</span>
                    </span>

                    <span className="ce-road" />
                  </div>
                </div>

                <h4 className="fw-bold mt-3 ce-title">Your Cart Is Empty</h4>
                <p className="ce-sub mb-4">
                  Looks like you haven't added anything yet — let's fix that.
                </p>

                <Button
                  className="rounded-pill px-4 fw-semibold ce-btn"
                  onClick={() => navigate('/products')}
                >
                  <span className="ce-btn-label">🛍 Continue Shopping</span>
                </Button>

                <div className="ce-perks">
                  <span>🚚 Free Delivery</span>
                  <span>↩ Easy Returns</span>
                  <span>🔒 Secure Payments</span>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {/* Items list */}
              <Col lg={8}>
                {items.map((item) => {
                  const isHauling = haulingIds.has(item._id);

                  const isCollapsing = collapsingIds.has(item._id);

                  const isUpdating = updatingIds.has(item._id);

                  /* Older carts predate the stock/price enrichment, so treat a
                     missing field as "no constraint known". */
                  const stock =
                    typeof item.stock === 'number' ? item.stock : Infinity;

                  const atStockLimit = item.quantity >= stock;

                  const priceChanged =
                    typeof item.currentPrice === 'number' &&
                    item.currentPrice !== item.price;

                  return (
                    <div
                      className={`cart-item-wrap${
                        isHauling ? ' cart-hauling' : ''
                      }${isCollapsing ? ' cart-collapse' : ''}`}
                      key={item._id}
                    >
                      {isHauling && renderHaul(item)}

                      {/* The shell is what the collapse squeezes shut once
                          the product has been carted off. */}
                      <div className="cart-item-shell">
                      <Card className="border-0 shadow-sm mb-4 cart-item">
                        <Card.Body className="p-3 p-md-4">
                      <Row className="align-items-center g-3 g-md-4">
                        {/* Image */}
                        <Col xs={4} md={3}>
                          <div className="cart-img-wrap">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="cart-img"
                              onClick={() =>
                                navigate(`/product/${item.productId}`)
                              }
                            />
                          </div>
                        </Col>

                        {/* Details */}
                        <Col xs={8} md={5}>
                          <h6
                            className="fw-bold cart-name mb-1"
                            onClick={() =>
                              navigate(`/product/${item.productId}`)
                            }
                          >
                            {item.name}
                          </h6>

                          {/* Real availability, straight from the product */}
                          <div className="mb-2">
                            {item.available === false ? (
                              <Badge bg="secondary" className="rounded-pill">
                                Unavailable
                              </Badge>
                            ) : stock <= 0 ? (
                              <Badge bg="danger" className="rounded-pill">
                                Out of Stock
                              </Badge>
                            ) : stock <= 5 ? (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="rounded-pill"
                              >
                                ⚠ Only {stock} left
                              </Badge>
                            ) : (
                              <Badge bg="success" className="rounded-pill">
                                ✓ In Stock
                              </Badge>
                            )}
                          </div>

                          <div className="cart-unit">
                            ₹{item.price.toLocaleString()} each
                          </div>

                          {/* Price moved since it was added */}
                          {priceChanged && (
                            <div
                              className={`cart-price-note ${
                                item.currentPrice < item.price
                                  ? 'is-down'
                                  : 'is-up'
                              }`}
                            >
                              {item.currentPrice < item.price ? '↓' : '↑'} Now ₹
                              {item.currentPrice.toLocaleString()}
                            </div>
                          )}
                        </Col>

                        {/* Quantity, price + actions */}
                        <Col xs={12} md={4} className="text-md-end">
                          {/* Quantity stepper — the cart had no way to
                              change quantity at all before. */}
                          <div className="cart-qty justify-content-md-end mb-2">
                            <button
                              type="button"
                              className="cart-qty-btn"
                              aria-label="Decrease quantity"
                              disabled={item.quantity <= 1 || isUpdating}
                              onClick={() =>
                                changeQuantity(item, item.quantity - 1)
                              }
                            >
                              −
                            </button>

                            <span className="cart-qty-value">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              className="cart-qty-btn"
                              aria-label="Increase quantity"
                              disabled={atStockLimit || isUpdating}
                              title={
                                atStockLimit
                                  ? `Only ${stock} in stock`
                                  : 'Increase quantity'
                              }
                              onClick={() =>
                                changeQuantity(item, item.quantity + 1)
                              }
                            >
                              +
                            </button>
                          </div>

                          <div className="fw-bold cart-line-total mb-2">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </div>

                          <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                            <Button
                              size="sm"
                              className="fw-semibold cart-act"
                              onClick={() =>
                                navigate(`/product/${item.productId}`)
                              }
                            >
                              <span className="cart-act-ico">👀</span> View
                            </Button>
                            <Button
                              size="sm"
                              className="fw-semibold cart-act cart-act-wish"
                              onClick={() => addToWishlist(item)}
                            >
                              <span className="cart-act-ico">❤️</span> Wishlist
                            </Button>
                            <Button
                              size="sm"
                              className="fw-semibold cart-act cart-act-remove"
                              onClick={() => removeItem(item._id)}
                            >
                              <span className="cart-act-ico">🗑</span> Remove
                            </Button>
                          </div>
                        </Col>
                      </Row>
                        </Card.Body>
                      </Card>
                      </div>
                    </div>
                  );
                })}
              </Col>

              {/* Order summary */}
              <Col lg={4}>
                <Card className="border-0 shadow-sm cart-summary">
                  <div className="cart-summary-header">
                    <h5 className="mb-0 fw-bold">🧾 Order Summary</h5>
                  </div>
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">
                        Subtotal ({items.length})
                      </span>
                      <span className="fw-semibold">
                        ₹{total.toLocaleString()}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Delivery</span>
                      <span className="fw-semibold text-success">FREE</span>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <span className="fw-bold fs-5">Total</span>
                      <span className="fw-bold fs-4 text-success">
                        ₹{total.toLocaleString()}
                      </span>
                    </div>

                    <Button
                      size="lg"
                      className="w-100 fw-bold cart-checkout-btn mb-2"
                      onClick={() => navigate('/checkout')}
                    >
                      Proceed To Checkout →
                    </Button>

                    <Button
                      variant="link"
                      className="w-100 text-decoration-none text-muted fw-semibold"
                      onClick={() => navigate('/products')}
                    >
                      ← Continue Shopping
                    </Button>

                    <div className="cart-trust mt-3">
                      🔒 Secure checkout · 🔄 Easy returns
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      <style>{cartStyles}</style>
    </>
  );
}

const cartStyles = `
  .cart-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 50px) 0;
  }

  /* Header */
  .cart-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, #ea580c);
    position: relative;
  }
  .cart-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
  }
  .cart-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 18%;
  }
  .cart-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
  }
  .cart-hero-text {
    opacity: 0.85;
  }
  .cart-count-badge {
    background: rgba(255,255,255,0.18) !important;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 30px;
    padding: 10px 18px;
    font-size: 1rem;
    font-weight: 600;
  }

  /* Cart items */
  .cart-item {
    position: relative;
    border-radius: 20px;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  }
  .cart-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 38px rgba(194, 65, 12, 0.16) !important;
  }

  /* ===== Removal: carted off =====
     Timed against HAUL_MS / COLLAPSE_MS in the component. One 2s run,
     played out entirely within the row:
       0.00-0.38s  a trolley rolls in from the left and parks under the product
       0.28-0.92s  the product drops out of the card into it
       0.92-2.00s  the trolley wheels away and off the edge of the row
     Then the empty row closes up.

     The product is a child of the trolley, so once it has landed the pair
     move together and only the trolley carries a travel path. */
  .cart-item-wrap {
    position: relative;
    /* 1fr → 0fr is what lets the row close without measuring its height */
    display: grid;
    grid-template-rows: 1fr;
  }
  .cart-item-shell {
    min-height: 0;
  }

  /* The card is left behind empty, greying out as its product is carted off */
  .cart-hauling .cart-item {
    pointer-events: none;
    animation: cartEmptyOut 2s ease forwards;
  }
  @keyframes cartEmptyOut {
    0%   { opacity: 1; filter: grayscale(0); transform: none; }
    12%  { transform: translateY(-3px); }
    38%  { opacity: 0.68; filter: grayscale(0.5); }
    100% { opacity: 0.22; filter: grayscale(0.85); transform: scale(0.978); }
  }
  /* Its thumbnail hands over to the copy riding in the trolley, timed to the
     moment that copy appears above the parked trolley */
  .cart-hauling .cart-img {
    animation: cartThumbLeave 0.22s ease 0.2s forwards;
  }
  @keyframes cartThumbLeave {
    to { opacity: 0; transform: scale(0.72); }
  }

  /* Beat two: the empty row glides shut */
  .cart-collapse {
    overflow: hidden;
    animation: cartCollapse 0.36s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  @keyframes cartCollapse {
    from { grid-template-rows: 1fr; }
    to   { grid-template-rows: 0fr; }
  }

  /* The stage. Clipped to the card itself — inset by the row's 24px bottom
     margin and rounded to match — so the trolley disappears cleanly off the
     edge of the row instead of overrunning the page. */
  .cart-haul-layer {
    position: absolute;
    inset: 0 0 24px 0;
    z-index: 6;
    pointer-events: none;
    overflow: hidden;
    border-radius: 20px;
  }

  /* Dashed ground the trolley runs along */
  .cart-haul-road {
    position: absolute;
    left: 3%;
    right: 0;
    bottom: 19%;
    height: 3px;
    border-radius: 3px;
    background-image: linear-gradient(90deg, #fdba74 0 14px, transparent 14px 28px);
    background-size: 28px 3px;
    opacity: 0;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 14%, #000 100%);
    mask-image: linear-gradient(90deg, transparent, #000 14%, #000 100%);
    animation: cartRoadShow 2s ease forwards, cartRoadScroll 1s linear infinite;
  }
  @keyframes cartRoadShow {
    0%, 3% { opacity: 0; }
    12%    { opacity: 0.85; }
    84%    { opacity: 0.85; }
    100%   { opacity: 0; }
  }
  @keyframes cartRoadScroll {
    to { background-position: 28px 0; }
  }

  /* The trolley. A fixed box rather than a bare glyph, so the product can be
     placed against something whose size does not depend on the emoji font. */
  .cart-haul-trolley {
    position: absolute;
    left: -18%;
    bottom: 19%;
    width: 48px;
    height: 48px;
    transform-origin: 50% 92%;
    filter: drop-shadow(0 6px 10px rgba(124,45,18,0.3));
    animation: cartTrolleyRun 2s forwards;
  }
  .cart-haul-glyph {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.7rem;
    line-height: 1;
  }
  /* The run out is held at a steady speed on purpose: the bob keyframes are
     spaced along it, and the default ease would re-accelerate between each
     pair, giving the trolley a lurching gait. linear keeps the roll even and
     leaves the bob to do the bouncing. */
  @keyframes cartTrolleyRun {
    0%   { left: -18%; opacity: 0; transform: rotate(4deg); animation-timing-function: cubic-bezier(0.2, 0.75, 0.3, 1); }
    6%   { opacity: 1; }
    14%  { left: 11%; transform: rotate(-3deg); }
    19%  { left: 13%; transform: rotate(0deg); }
    /* waits, then takes the weight as the product lands */
    32%  { left: 13%; transform: translateY(0) scaleY(1) rotate(0deg); }
    37%  { left: 13%; transform: translateY(5px) scaleY(0.88) rotate(-2deg); }
    41%  { left: 13%; transform: translateY(0) scaleY(1.03) rotate(1deg); }
    /* and wheels it away, bouncing over the road */
    46%  { left: 15%; transform: translateY(0) scaleY(1) rotate(0deg); animation-timing-function: linear; }
    56%  { transform: translateY(-5px) rotate(-4deg); animation-timing-function: linear; }
    68%  { transform: translateY(0) rotate(3deg); animation-timing-function: linear; }
    80%  { transform: translateY(-5px) rotate(-4deg); animation-timing-function: linear; }
    92%  { transform: translateY(0) rotate(2deg); animation-timing-function: linear; }
    97%  { opacity: 1; }
    100% { left: 124%; opacity: 0; transform: translateY(0) rotate(0deg); }
  }

  /* Speed dashes off the back, only while it is actually running */
  .cart-haul-line {
    position: absolute;
    height: 2px;
    border-radius: 2px;
    background: rgba(234,88,12,0.6);
    opacity: 0;
    animation: cartHaulLine 2s ease forwards;
  }
  .cart-haul-line-1 { top: 36%; left: -16px; width: 15px; }
  .cart-haul-line-2 { top: 58%; left: -11px; width: 10px; }
  @keyframes cartHaulLine {
    0%, 46%   { opacity: 0; transform: translateX(0) scaleX(0.3); }
    55%       { opacity: 0.9; transform: translateX(-9px) scaleX(1); }
    90%       { opacity: 0.9; transform: translateX(-9px) scaleX(1); }
    97%, 100% { opacity: 0; transform: translateX(0) scaleX(0.3); }
  }

  /* The product: waits over the card, drops into the basket, then rides.
     Positioned against the trolley box, so these offsets are what to nudge
     if it does not sit squarely in the basket. */
  .cart-haul-product {
    position: absolute;
    left: 10px;
    bottom: 16px;
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 5px;
    opacity: 0;
    filter: drop-shadow(0 4px 8px rgba(124,45,18,0.35));
    animation: cartProductDrop 2s forwards;
  }
  /* Mirrors cartTrolleyRun beat for beat — it lands just before the trolley
     dips at 37%, and bobs on the same keyframes during the run out. */
  @keyframes cartProductDrop {
    /* held over the card at thumbnail size while the trolley rolls in */
    0%, 9% { opacity: 0; transform: translate(0, -36px) scale(3.4) rotate(0deg); }
    14%    { opacity: 1; transform: translate(0, -40px) scale(3.2) rotate(-4deg); animation-timing-function: cubic-bezier(0.45, 0, 0.75, 0.45); }
    /* tips over the rim and falls in */
    23%    { transform: translate(1px, -44px) scale(2.6) rotate(7deg); }
    36%    { transform: translate(0, 0) scale(1) rotate(-9deg); }
    41%    { transform: translate(0, 3px) scale(1) rotate(-4deg); }
    46%    { transform: translate(0, 0) scale(1) rotate(0deg); }
    /* rides along, bobbing with the wheels */
    56%    { transform: translate(0, -3px) rotate(-4deg); }
    68%    { transform: translate(0, 0) rotate(3deg); }
    80%    { transform: translate(0, -3px) rotate(-4deg); }
    92%    { transform: translate(0, 0) rotate(2deg); }
    97%    { opacity: 1; }
    100%   { opacity: 0; }
  }

  /* Keep the animation buttery on lower-power devices */
  .cart-haul-trolley,
  .cart-haul-product,
  .cart-haul-line {
    will-change: transform, opacity;
  }
  @media (max-width: 575.98px) {
    .cart-haul-trolley { width: 38px; height: 38px; }
    .cart-haul-glyph { font-size: 2.1rem; }
    .cart-haul-product { width: 22px; height: 22px; left: 8px; bottom: 13px; }
  }

  /* The journey is the whole point of this animation, so with motion reduced
     there is nothing to shorten — the card simply goes. */
  @media (prefers-reduced-motion: reduce) {
    .cart-haul-layer { display: none; }
    .cart-hauling .cart-item {
      animation: cartEmptyOut 0.25s ease forwards;
    }
    .cart-hauling .cart-img { animation: none; }
  }
  /* ================= Empty cart scene =================
     One 4s loop drives everything: the trolley rolls in, catches two
     products, bumps the Continue Shopping button, then rolls off. Every
     animation below shares that 4s so the beats stay in sync. */
  .cart-empty {
    position: relative;
    overflow: hidden;
    padding-bottom: 8px;
  }

  .ce-stage {
    position: relative;
    height: 150px;
    margin-bottom: 4px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /* Products drifting in the background */
  .ce-float {
    position: absolute;
    font-size: 1.5rem;
    opacity: 0.5;
    filter: saturate(1.15);
    pointer-events: none;
    animation: ceFloat 7s ease-in-out infinite;
  }
  .ce-float-1 { left: 10%; top: 14%; animation-delay: -0.4s; }
  .ce-float-2 { left: 24%; top: 52%; font-size: 1.15rem; animation-delay: -2.1s; }
  .ce-float-3 { right: 13%; top: 10%; animation-delay: -1.2s; }
  .ce-float-5 { left: 46%; top: 2%;  font-size: 1.05rem; animation-delay: -5s; }
  @keyframes ceFloat {
    0%, 100% { transform: translateY(0) rotate(-6deg); opacity: 0.35; }
    50%      { transform: translateY(-16px) rotate(8deg); opacity: 0.7; }
  }

  .ce-track {
    position: relative;
    width: 100%;
    height: 104px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /* The trolley */
  .ce-trolley {
    position: relative;
    font-size: 4rem;
    line-height: 1;
    display: inline-block;
    transform-origin: 50% 92%;
    filter: drop-shadow(0 6px 10px rgba(124,45,18,0.22));
    animation: ceRoll 6s linear infinite;
  }
  /* Straight run right → left at a steady speed, wheels bouncing over the
     road the whole way. */
  @keyframes ceRoll {
    0%   { transform: translateX(340px) translateY(0) rotate(5deg); opacity: 0; }
    8%   { opacity: 1; }
    15%  { transform: translateX(230px) translateY(-6px) rotate(-3deg); }
    25%  { transform: translateX(170px) translateY(0) rotate(3deg); }
    35%  { transform: translateX(105px) translateY(-7px) rotate(-4deg); }
    45%  { transform: translateX(45px) translateY(0) rotate(2deg); }
    55%  { transform: translateX(-20px) translateY(-7px) rotate(-4deg); }
    65%  { transform: translateX(-90px) translateY(0) rotate(3deg); }
    75%  { transform: translateX(-160px) translateY(-6px) rotate(-3deg); }
    85%  { transform: translateX(-230px) translateY(0) rotate(2deg); }
    92%  { opacity: 1; }
    100% { transform: translateX(-320px) translateY(0) rotate(-4deg); opacity: 0; }
  }

  /* Ground shadow, tracks the trolley and squashes on landing */
  .ce-shadow {
    position: absolute;
    bottom: 4px;
    width: 74px;
    height: 12px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(124,45,18,0.30), transparent);
    filter: blur(2px);
    animation: ceShadow 6s linear infinite;
  }
  /* Same run as the trolley; shrinks each time the wheels leave the road. */
  @keyframes ceShadow {
    0%   { transform: translateX(340px) scale(0.85); opacity: 0; }
    8%   { opacity: 0.9; }
    15%  { transform: translateX(230px) scale(0.78); opacity: 0.55; }
    25%  { transform: translateX(170px) scale(1); opacity: 0.9; }
    35%  { transform: translateX(105px) scale(0.76); opacity: 0.5; }
    45%  { transform: translateX(45px) scale(1); opacity: 0.9; }
    55%  { transform: translateX(-20px) scale(0.76); opacity: 0.5; }
    65%  { transform: translateX(-90px) scale(1); opacity: 0.9; }
    75%  { transform: translateX(-160px) scale(0.78); opacity: 0.55; }
    85%  { transform: translateX(-230px) scale(1); opacity: 0.9; }
    92%  { opacity: 0.8; }
    100% { transform: translateX(-320px) scale(0.85); opacity: 0; }
  }

  /* Sparkle trail off the back of the trolley */
  .ce-spark {
    position: absolute;
    font-size: 0.85rem;
    right: -6px;
    top: 24%;
    animation: ceSpark 6s ease-out infinite;
  }
  .ce-spark-2 { right: -2px; top: 62%; font-size: 0.7rem; animation-delay: -0.45s; }
  @keyframes ceSpark {
    0%, 6%   { opacity: 0; transform: translate(0,0) scale(0.4); }
    18%      { opacity: 1; transform: translate(14px, -6px) scale(1); }
    34%      { opacity: 0; transform: translate(30px, -14px) scale(0.4); }
    100%     { opacity: 0; }
  }

  /* Products dropping into the trolley as it parks */
  .ce-drop {
    position: absolute;
    bottom: 46px;
    font-size: 1.35rem;
    opacity: 0;
    pointer-events: none;
    animation: ceDrop 6s ease-in infinite;
  }
  .ce-drop-1 { margin-left: -14px; }
  .ce-drop-2 { margin-left: 18px; animation-delay: -0.22s; font-size: 1.15rem; }
  /* Timed so they land in the trolley exactly as it crosses the middle */
  @keyframes ceDrop {
    0%, 36%  { opacity: 0; transform: translateY(-70px) rotate(-25deg) scale(0.7); }
    40%      { opacity: 1; transform: translateY(-56px) rotate(-12deg) scale(1); }
    50%      { opacity: 1; transform: translateY(0) rotate(10deg) scale(1); }
    56%      { opacity: 0; transform: translateY(6px) rotate(14deg) scale(0.85); }
    100%     { opacity: 0; }
  }

  /* Dashed road scrolling under the wheels */
  .ce-road {
    position: absolute;
    left: 8%;
    right: 8%;
    bottom: 0;
    height: 3px;
    border-radius: 3px;
    background-image: linear-gradient(90deg, #fdba74 0 16px, transparent 16px 30px);
    background-size: 30px 3px;
    opacity: 0.75;
    animation: ceRoad 1.3s linear infinite;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 18%, #000 82%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 18%, #000 82%, transparent);
  }
  /* Dashes scroll rightwards so the ground reads as passing under a
     right-to-left moving trolley. */
  @keyframes ceRoad {
    to { background-position: 30px 0; }
  }

  /* Copy */
  .ce-title {
    font-size: clamp(1.4rem, 3.4vw, 1.9rem);
    background: linear-gradient(100deg, #7c2d12, #ea580c 45%, #f59e0b 70%, #7c2d12);
    background-size: 220% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ceShine 5s linear infinite;
  }
  @keyframes ceShine {
    to { background-position: 220% center; }
  }
  .ce-sub {
    color: #78716c;
    max-width: 380px;
    margin-inline: auto;
  }

  /* Button — gradient, glow, and a shimmer sweep */
  .ce-btn {
    position: relative;
    overflow: hidden;
    border: none;
    padding-top: 12px;
    padding-bottom: 12px;
    font-size: 1.02rem;
    color: #fff;
    background: linear-gradient(120deg, #c2410c, #ea580c 55%, #f59e0b);
    background-size: 180% auto;
    animation: ceBtnBump 6s ease-in-out infinite;
    transition: background-position 0.4s ease, transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ce-btn:hover, .ce-btn:focus, .ce-btn:active {
    background: linear-gradient(120deg, #c2410c, #ea580c 55%, #f59e0b);
    background-size: 180% auto;
    background-position: right center;
    color: #fff;
    box-shadow: 0 16px 32px rgba(234, 88, 12, 0.42) !important;
  }
  .ce-btn-label {
    position: relative;
    z-index: 2;
  }
  /* light sweeping across the button */
  .ce-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: 120%;
    width: 45%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent);
    transform: skewX(-20deg);
    animation: ceSheen 6s ease-in-out infinite;
  }
  /* Sweeps the same way the trolley travels */
  @keyframes ceSheen {
    0%, 46%  { left: 120%; }
    68%      { left: -60%; }
    100%     { left: -60%; }
  }
  /* Reacts as the trolley rolls over it, mid-run */
  @keyframes ceBtnBump {
    0%, 44%, 100% { transform: scale(1) translateY(0); box-shadow: 0 8px 20px rgba(234,88,12,0.30); }
    52%           { transform: scale(1.08) translateY(-2px); box-shadow: 0 18px 34px rgba(234,88,12,0.45); }
    60%           { transform: scale(0.98) translateY(0); }
    66%           { transform: scale(1.03); }
  }

  /* Trust strip */
  .ce-perks {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 10px;
    margin-top: 22px;
  }
  .ce-perks span {
    font-size: 0.78rem;
    font-weight: 600;
    color: #9a3412;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 30px;
    padding: 6px 14px;
  }

  /* Note: deliberately no hover speed-up. Moving the pointer down toward the
     button would hover the card and jerk the trolley into double speed, so the
     roll stays at one calm pace throughout. */

  @media (max-width: 575.98px) {
    .ce-stage { height: 122px; }
    .ce-track { height: 86px; }
    .ce-trolley { font-size: 3rem; }
    .ce-float { font-size: 1.15rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ce-trolley, .ce-shadow, .ce-road, .ce-btn, .ce-btn::after,
    .ce-float, .ce-spark, .ce-drop, .ce-title {
      animation: none;
    }
    .ce-trolley { opacity: 1; transform: none; }
    .ce-shadow { opacity: 0.8; transform: none; }
    .ce-spark, .ce-drop { display: none; }
    .ce-title { -webkit-text-fill-color: #7c2d12; }
  }

  /* ===== Quantity stepper ===== */
  .cart-qty {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border-radius: 30px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
  }
  .cart-qty-btn {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: #fff;
    color: #c2410c;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(124,45,18,0.12);
    transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
  }
  .cart-qty-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    transform: scale(1.08);
  }
  .cart-qty-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }
  .cart-qty-value {
    min-width: 34px;
    text-align: center;
    font-weight: 800;
    font-size: 0.95rem;
    color: #7c2d12;
    font-variant-numeric: tabular-nums;
  }

  .cart-unit {
    font-size: 0.8rem;
    color: #78716c;
    font-weight: 600;
  }
  /* Price moved since the item was added */
  .cart-price-note {
    margin-top: 3px;
    font-size: 0.74rem;
    font-weight: 800;
  }
  .cart-price-note.is-down { color: #16a34a; }
  .cart-price-note.is-up   { color: #b45309; }

  /* Line total */
  .cart-line-total {
    font-size: 1.3rem;
    background: linear-gradient(120deg, #15803d, #22c55e);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  /* Row actions */
  /* ===== Cart row actions =====
     Each button carries its own colour instead of only revealing one on
     hover. The brass hairline and the inset top highlight are shared, so the
     three read as one set of temple pieces rather than three loose buttons. */
  .cart-act {
    border: 1px solid rgba(253, 230, 138, 0.55);
    border-radius: 12px;
    color: #fff7ed;
    /* View — deep violet through to magenta */
    background: linear-gradient(135deg, #4c1d95 0%, #6d28d9 48%, #9333ea 100%);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.22),
      0 6px 16px rgba(109, 40, 217, 0.3);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease;
  }
  .cart-act:hover, .cart-act:focus, .cart-act:active {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 48%, #a855f7 100%) !important;
    border-color: #fde68a !important;
    color: #fff !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      0 12px 24px rgba(124, 58, 237, 0.45);
  }

  /* Wishlist — rose through to crimson */
  .cart-act-wish {
    background: linear-gradient(135deg, #9f1239 0%, #be123c 48%, #f43f5e 100%);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.22),
      0 6px 16px rgba(190, 18, 60, 0.32);
  }
  .cart-act-wish:hover, .cart-act-wish:focus, .cart-act-wish:active {
    background: linear-gradient(135deg, #be123c 0%, #e11d48 48%, #fb7185 100%) !important;
    border-color: #fde68a !important;
    color: #fff !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      0 12px 24px rgba(225, 29, 72, 0.45);
  }

  /* Remove — deliberately the quietest of the three, since it destroys
     something. Stone at rest, deepening to maroon only when reached for. */
  .cart-act-remove {
    border-color: rgba(214, 211, 209, 0.9);
    color: #f5f5f4;
    background: linear-gradient(135deg, #44403c 0%, #57534e 50%, #78716c 100%);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      0 6px 14px rgba(68, 64, 60, 0.28);
  }
  .cart-act-remove:hover, .cart-act-remove:focus, .cart-act-remove:active {
    background: linear-gradient(135deg, #7f1d1d 0%, #b91c1c 55%, #dc2626 100%) !important;
    border-color: rgba(254, 202, 202, 0.9) !important;
    color: #fff !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 12px 24px rgba(185, 28, 28, 0.45);
  }

  /* Icons come alive on hover — a look, a heartbeat, a shake */
  .cart-act-ico {
    display: inline-block;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .cart-act:hover .cart-act-ico { transform: scale(1.22); }
  .cart-act-wish:hover .cart-act-ico {
    animation: cartHeartbeat 0.95s ease-in-out infinite;
  }
  @keyframes cartHeartbeat {
    0%, 100% { transform: scale(1); }
    22%      { transform: scale(1.34); }
    38%      { transform: scale(1.06); }
    58%      { transform: scale(1.26); }
  }
  .cart-act-remove { transform-origin: center; }
  .cart-act-remove:hover .cart-act-ico {
    animation: cartShake 0.6s ease-in-out infinite;
  }
  @keyframes cartShake {
    0%, 100% { transform: rotate(0deg); }
    30%      { transform: rotate(-15deg); }
    65%      { transform: rotate(12deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .cart-act-wish:hover .cart-act-ico,
    .cart-act-remove:hover .cart-act-ico { animation: none; }
    .cart-act:hover, .cart-act:focus, .cart-act:active { transform: none; }
  }

  .cart-img-wrap {
    background: #f8fafc;
    border: 1px solid #f1e9d8;
    border-radius: 16px;
    overflow: hidden;
  }
  .cart-img {
    width: 100%;
    height: clamp(120px, 15vw, 160px);
    object-fit: contain;
    padding: 10px;
    cursor: pointer;
    transition: transform 0.3s ease;
  }
  .cart-img:hover {
    transform: scale(1.08);
  }
  .cart-name {
    cursor: pointer;
    font-size: 1.2rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .cart-name:hover {
    color: #ea580c;
  }
  .cart-item .badge { font-size: 0.78rem; padding: 5px 11px; }

  /* Order summary */
  .cart-summary {
    border-radius: 18px;
    overflow: hidden;
    position: sticky;
    top: 90px;
  }
  .cart-summary-header {
    background: linear-gradient(120deg, #c2410c, #ea580c);
    color: #fff;
    padding: 16px 24px;
  }
  .cart-checkout-btn {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #16a34a, #15803d);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .cart-checkout-btn:hover {
    background: linear-gradient(135deg, #15803d, #166534);
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(22,163,74,0.4);
  }
  .cart-trust {
    text-align: center;
    font-size: 0.78rem;
    color: #94a3b8;
  }

  @media (max-width: 991.98px) {
    .cart-summary { position: static; }
  }
`;

export default CartPage;
